import { Card, CardContent, CardHeader } from '@ophelos/ui/components/card-layer';
import { Loader } from '@ophelos/ui/components/loader';
import { MessageSquare } from 'lucide-react';
import { useSupportComments } from '../hooks/useSupportQueries';

interface TicketUpdatesSectionProps {
  issueNumber: number;
}

export function TicketUpdatesSection({ issueNumber }: TicketUpdatesSectionProps) {
  const { data: comments = [], isLoading, error } = useSupportComments(issueNumber, { refetchInterval: 60_000 });

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <MessageSquare className="size-4" />
          Ticket updates
        </h3>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader size="sm" />
            Loading comments...
          </div>
        ) : error ? (
          <p className="text-sm text-destructive py-2">{error instanceof Error ? error.message : 'Failed to load comments'}</p>
        ) : comments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No comments yet.</p>
        ) : (
          <div className="space-y-4 max-h-[300px] overflow-y-auto slim-scrollbar">
            {comments.map((c) => (
              <div key={c.id} className="border-b pb-3 last:border-0 last:pb-0">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <span className="font-medium text-foreground">{c.user ?? 'Unknown'}</span>
                  <span>·</span>
                  <span>{formatDate(c.created_at)}</span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{c.body}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
